import type { CutoutDefinition } from "@project/emerge";
import { Sides } from "@project/emerge-types";
import without from "lodash/without";
import * as THREE from "three";
import { Alignment } from "../enum/Alignment";
import type { ConnectorCategory } from "../enum/ConnectorType";
import type { EdgeIndex } from "../enum/EdgeIndex";
import { EdgeType } from "../enum/EdgeType";
import { OrientationOnRect } from "../enum/OrientationOnRect";
import type { ConnectorInfos } from "../interfaces/ConnectorInfos";
import type { CutProperties } from "../interfaces/CutProperties";
import type { HingeType } from "../interfaces/HingeType";
import type { LockType } from "../interfaces/LockType";
import type { ProfileType } from "../interfaces/ProfileType";
import type { EndpointInfos } from "./EndpointInfos";
import type { Rect } from "./Rect";
import { RectPropertiesBySide } from "./RectProperties";

// const MILLING_EXTRA = 3; // make cutouts larger by half milling

export const AlignmentNames = {
  [OrientationOnRect.HORIZONTAL]: {
    [Alignment.START]: "Links",
    [Alignment.CENTER]: "Mittig",
    [Alignment.END]: "Rechts",
  },
  [OrientationOnRect.VERTICAL]: {
    [Alignment.START]: "Unten",
    [Alignment.CENTER]: "Mittig",
    [Alignment.END]: "Oben",
  },
};
// describes an edge of the case
export class EdgeInfos {
  get center() {
    const result = new THREE.Vector3();
    result.addVectors(this.start.center, this.end.center);
    result.multiplyScalar(0.5);
    return result;
  }
  get outerCenter() {
    const result = new THREE.Vector3();
    result.addVectors(this.start.outerPos, this.end.outerPos);
    result.multiplyScalar(0.5);
    return result;
  }
  get innerCenter() {
    const result = new THREE.Vector3();
    result.addVectors(this.start.innerPos, this.end.innerPos);
    result.multiplyScalar(0.5);
    return result;
  }
  get leftOffset(): number {
    const dir = new THREE.Vector3()
      .subVectors(this.start.innerPos, this.end.innerPos)
      .normalize();
    return new THREE.Vector3()
      .subVectors(this.start.originalPos, this.start.innerPos)
      .dot(dir);
  }
  get rightOffset(): number {
    const dir = new THREE.Vector3()
      .subVectors(this.start.innerPos, this.end.innerPos)
      .normalize();
    return new THREE.Vector3()
      .subVectors(this.end.originalPos, this.end.innerPos)
      .dot(dir);
  }
  get inwards(): THREE.Vector3 {
    const primarySide = this.sides[0];
    if (primarySide === undefined) {
      return new THREE.Vector3();
    }
    const sideProps = RectPropertiesBySide[primarySide];
    if (!sideProps) {
      return new THREE.Vector3();
    }
    return sideProps.normalDir.clone().cross(this.direction);
  }
  get innerLength(): number {
    return this.start.innerPos.distanceTo(this.end.innerPos);
  }
  get outerLength(): number {
    return this.start.outerPos.distanceTo(this.end.outerPos);
  }
  get endPoints(): [EndpointInfos, EndpointInfos] {
    return [this.start, this.end];
  }
  get direction(): THREE.Vector3 {
    return this.end.innerPos.clone().sub(this.start.innerPos).normalize();
  }
  get dir(): "x" | "y" | "z" {
    const direction = this.direction;
    if (direction.x !== 0) {
      return "x";
    } else if (direction.y !== 0) {
      return "y";
    } else {
      return "z";
    }
  }
  public static sortConnectedEdges(edges: EdgeInfos[]): EdgeInfos[] {
    if (edges.length === 0) {
      return [];
    }
    const first = edges[0];
    if (!first) {
      return [];
    }
    const orderedEdges: EdgeInfos[] = [first];
    const maxIterations = edges.length * 2; // Safety limit
    let iterations = 0;
    while (orderedEdges.length < edges.length) {
      if (++iterations > maxIterations) {
        console.warn(
          `sortConnectedEdges: exceeded max iterations (${maxIterations}), returning partial result`,
        );
        return orderedEdges;
      }
      let changes = false;
      for (const edge of edges) {
        const firstOrderedEdge = orderedEdges[0];
        const lastOrderedEdge = orderedEdges[orderedEdges.length - 1];

        if (firstOrderedEdge && edge.pointsTo(firstOrderedEdge)) {
          orderedEdges.unshift(edge);
          changes = true;
          break;
        } else if (lastOrderedEdge?.pointsTo(edge)) {
          orderedEdges.push(edge);
          changes = true;
          break;
        }
      }
      if (!changes) {
        return [];
      }
    }
    return orderedEdges;
  }

  public group: string;
  public cutName: string | null;
  public cut: CutProperties | null = null;
  public isOriginal: boolean;
  public inverted = false;
  public orientationOnRect: OrientationOnRect;
  public originalEdgeLength: number;
  public connectors: Array<{
    flip: boolean;
    partSelection: HingeType | LockType;
    position: THREE.Vector3;
    type: ConnectorCategory;
    sourceOptionUuid: string;
    n: number;
  }> = [];
  public start: EndpointInfos;
  public end: EndpointInfos;
  public get type(): EdgeType {
    if (this.start.isOriginal && this.end.isOriginal) {
      return EdgeType.ORIGINAL;
    } else if (this.start.isOriginal || this.end.isOriginal) {
      return EdgeType.SHORTENED;
    } else {
      return this.sides.length === 2 ? EdgeType.SHORTENED : EdgeType.FROMCUT;
    }
  }
  public sides: Sides[];
  public rects: Rect[] = [];
  public cutReference: Sides;
  public index: EdgeIndex;
  constructor(options: {
    group: string;
    isOriginal: boolean;
    end: EndpointInfos;
    start: EndpointInfos;
    sides: Sides[];
    cutReference: Sides;
    index: EdgeIndex;
  }) {
    this.group = options.group;
    this.cutName = null;
    this.isOriginal = options.isOriginal;
    this.end = options.end;
    this.start = options.start;
    this.originalEdgeLength = this.outerLength;
    this.sides = options.sides;
    this.cutReference = options.cutReference;
    this.index = options.index;
    this.orientationOnRect =
      options.index % 2 === 1
        ? OrientationOnRect.HORIZONTAL
        : OrientationOnRect.VERTICAL;
  }
  public get cuttingConnectors() {
    const hasConnectorWithCut =
      this.connectors.filter((c) => this.getConnectorCut(c) > 0).length > 0;
    return this.connectors.filter((c, n) => {
      // if there is no connector with cut on this segment, the first and last connectors will be cut anyways
      // otherwise they are only cut if they need to
      if (n === 0 || n === this.connectors.length - 1) {
        return !hasConnectorWithCut || this.getConnectorCut(c) > 0;
      }
      if (this.getConnectorCut(c) > 0) {
        return true;
      }
      return false;
    });
  }

  private getConnectorCut(connector: {
    partSelection: HingeType | LockType;
  }): number {
    // Check if partSelection has cut property directly (for backward compatibility)
    if (
      "cut" in connector.partSelection &&
      typeof connector.partSelection.cut === "number"
    ) {
      return connector.partSelection.cut;
    }
    return 0;
  }
  public getCutPosition(index: number, front = false): THREE.Vector3 {
    const connectors = this.cuttingConnectors;
    if (index === 0) {
      return this.start.outerPos;
    }
    // this.start.originalOuterPos.lerp();
    if (index === connectors.length + 1) {
      return this.end.outerPos;
    }
    const connectorEntry = connectors[index - 1];
    if (!connectorEntry) {
      return this.end.outerPos;
    }
    const { partSelection, position } = connectorEntry;
    const cut = this.getConnectorCut({ partSelection });
    if (cut === 0) {
      return position;
    } else {
      return position
        .clone()
        .add(this.direction.clone().multiplyScalar(front ? -cut / 2 : cut / 2));
    }
  }
  // move an edge in one direction
  public move(newCoord: number, direction: Sides, cutName: string) {
    // update which sides touch this
    const props = RectPropertiesBySide[direction];
    if (!props) return;

    const { normal } = props;
    this.sides = without(this.sides, direction);
    this.isOriginal = false;
    this.start.isOriginal = false;

    // Korrekt Vector3-Koordinaten aktualisieren
    if (normal === "x") {
      this.start.innerPos.setX(newCoord);
      this.start.outerPos.setX(newCoord);
      this.end.innerPos.setX(newCoord);
      this.end.outerPos.setX(newCoord);
    } else if (normal === "y") {
      this.start.innerPos.setY(newCoord);
      this.start.outerPos.setY(newCoord);
      this.end.innerPos.setY(newCoord);
      this.end.outerPos.setY(newCoord);
    } else if (normal === "z") {
      this.start.innerPos.setZ(newCoord);
      this.start.outerPos.setZ(newCoord);
      this.end.innerPos.setZ(newCoord);
      this.end.outerPos.setZ(newCoord);
    }

    this.start.cutsAffectedBy.push(cutName);
    this.start.sides = without(this.start.sides, direction);
    this.end.isOriginal = false;
    this.cutReference = direction;
    this.end.cutsAffectedBy.push(cutName);
    this.end.sides = without(this.end.sides, direction);
  }
  public getProfileCutout(_profiles: ProfileType): CutoutDefinition | null {
    // const { gapWidth, extra, height: profileHeight, strength } = profiles;
    // const deltaW = gapWidth - settings.exteriorOptions.strength + strength;
    // if (this.type === EdgeType.FROMCUT) {
    //     return {
    //         orientation: 0,
    //         position: this.center,
    //         shape: new MakerJs.models.Rectangle(
    //             2 * deltaW,
    //             2 * (profileHeight + extra - strength + MILLING_EXTRA),
    //         ),
    //     };
    // }
    return null;
  }
  public addConnector(connector: ConnectorInfos): boolean {
    const primarySide = this.sides[0];
    if (primarySide === undefined) {
      return false;
    }
    const props = RectPropertiesBySide[primarySide];
    if (!props) return false;

    const { vertical, horizontal } = props;
    const coord =
      this.orientationOnRect === OrientationOnRect.VERTICAL
        ? vertical
        : horizontal;
    const firstRect = this.rects[0];
    if (!firstRect) {
      return false;
    }
    // coordinate to work on
    const p = firstRect.resolvePosition1d(
      connector.position,
      this.orientationOnRect,
      this.getConnectorCut(connector) / 2,
    );
    // check if the connector sits on this part of the edge
    if (p !== null) {
      const pos3d = this.start.outerPos.clone();
      if (coord === "x") pos3d.setX(p);
      else if (coord === "y") pos3d.setY(p);
      else if (coord === "z") pos3d.setZ(p);
      this.connectors.push({
        flip: connector.flip,
        partSelection: connector.partSelection,
        position: pos3d,
        type: connector.connectorType,
        sourceOptionUuid: connector.sourceOptionUuid,
        n: connector.position.n,
      });
    } else {
      return false;
    }
    // make sure the connectors are in the correct order
    this.connectors.sort(
      (a, b) =>
        this.start.outerPos.distanceTo(a.position) -
        this.start.outerPos.distanceTo(b.position),
    );
    return true;
  }
  /**
   * Gibt die Seite zurück, die auf der linken Hand liegt, wenn man die Kante entlang geht
   *
   * @param reverseDirection Gibt an, ob die Richtung umgedreht werden soll
   * @returns Die linke Seite der Kante
   */
  public getLeftHandSide(reverseDirection = false): Sides {
    const isReversed = reverseDirection;

    // Orientiere uns am Punkt mit 3 Seiten (Ecke)
    const startPoint = isReversed ? this.end : this.start;
    const endPoint = isReversed ? this.start : this.end;

    // Die dritte Seite am Eckpunkt ist die Seite, von der wir kommen
    let comesFrom = startPoint.sides.find((side) => !this.sides.includes(side));

    // wenn comesFrom nicht gefunden wurde, dann müssen wir comesFrom berechnen, indem wir die position von startPoint und endPoint vergleichen
    // und die Seite bestimmen, von der wir kommen
    if (comesFrom === undefined) {
      const startPointPos = startPoint.outerPos;
      const endPointPos = endPoint.outerPos;
      const direction = endPointPos.clone().sub(startPointPos).normalize();

      // Bestimme die dominante Richtung (x, y oder z)
      const absX = Math.abs(direction.x);
      const absY = Math.abs(direction.y);
      const absZ = Math.abs(direction.z);

      // Finde die größte Komponente
      const maxAbs = Math.max(absX, absY, absZ);

      // Bestimme die Seite basierend auf der dominanten Richtung und ihrem Vorzeichen
      if (maxAbs === absX) {
        comesFrom = direction.x > 0 ? Sides.LEFT : Sides.RIGHT;
      } else if (maxAbs === absY) {
        comesFrom = direction.y > 0 ? Sides.BOTTOM : Sides.TOP;
      } else {
        comesFrom = direction.z > 0 ? Sides.BACK : Sides.FRONT;
      }
    }

    // Bei Kanten gibt es immer zwei gemeinsame Seiten (this.sides)
    if (this.sides.length !== 2) {
      // Fallback, sollte selten passieren
      return this.sides[0] || Sides.LEFT;
    }

    // Vereinfachter Ansatz: direkte Prüfung für jede mögliche Kombination
    let result: Sides | undefined;

    // TOP-Ecke
    if (comesFrom === Sides.TOP) {
      if (this.sides.includes(Sides.LEFT) && this.sides.includes(Sides.FRONT))
        result = Sides.FRONT;
      if (this.sides.includes(Sides.LEFT) && this.sides.includes(Sides.BACK))
        result = Sides.LEFT;
      if (this.sides.includes(Sides.RIGHT) && this.sides.includes(Sides.FRONT))
        result = Sides.RIGHT;
      if (this.sides.includes(Sides.RIGHT) && this.sides.includes(Sides.BACK))
        result = Sides.BACK;
    }

    // BOTTOM-Ecke
    if (comesFrom === Sides.BOTTOM) {
      if (this.sides.includes(Sides.LEFT) && this.sides.includes(Sides.FRONT))
        result = Sides.LEFT;
      if (this.sides.includes(Sides.LEFT) && this.sides.includes(Sides.BACK))
        result = Sides.BACK;
      if (this.sides.includes(Sides.RIGHT) && this.sides.includes(Sides.FRONT))
        result = Sides.FRONT;
      if (this.sides.includes(Sides.RIGHT) && this.sides.includes(Sides.BACK))
        result = Sides.RIGHT;
    }

    // LEFT-Ecke
    if (comesFrom === (Sides.LEFT as unknown as Sides)) {
      if (this.sides.includes(Sides.BOTTOM) && this.sides.includes(Sides.FRONT))
        result = Sides.FRONT;
      if (this.sides.includes(Sides.BOTTOM) && this.sides.includes(Sides.BACK))
        result = Sides.BOTTOM;
      if (this.sides.includes(Sides.TOP) && this.sides.includes(Sides.FRONT))
        result = Sides.TOP;
      if (this.sides.includes(Sides.TOP) && this.sides.includes(Sides.BACK))
        result = Sides.BACK;
    }

    // RIGHT-Ecke
    if (comesFrom === Sides.RIGHT) {
      if (this.sides.includes(Sides.BOTTOM) && this.sides.includes(Sides.FRONT))
        result = Sides.BOTTOM;
      if (this.sides.includes(Sides.BOTTOM) && this.sides.includes(Sides.BACK))
        result = Sides.BACK;
      if (this.sides.includes(Sides.TOP) && this.sides.includes(Sides.FRONT))
        result = Sides.FRONT;
      if (this.sides.includes(Sides.TOP) && this.sides.includes(Sides.BACK))
        result = Sides.TOP;
    }

    // FRONT-Ecke
    if (comesFrom === Sides.FRONT) {
      if (this.sides.includes(Sides.BOTTOM) && this.sides.includes(Sides.LEFT))
        result = Sides.BOTTOM;
      if (this.sides.includes(Sides.BOTTOM) && this.sides.includes(Sides.RIGHT))
        result = Sides.RIGHT;
      if (this.sides.includes(Sides.TOP) && this.sides.includes(Sides.LEFT))
        result = Sides.LEFT;
      if (this.sides.includes(Sides.TOP) && this.sides.includes(Sides.RIGHT))
        result = Sides.TOP;
    }

    // BACK-Ecke
    if (comesFrom === Sides.BACK) {
      if (this.sides.includes(Sides.BOTTOM) && this.sides.includes(Sides.LEFT))
        result = Sides.LEFT;
      if (this.sides.includes(Sides.BOTTOM) && this.sides.includes(Sides.RIGHT))
        result = Sides.BOTTOM;
      if (this.sides.includes(Sides.TOP) && this.sides.includes(Sides.LEFT))
        result = Sides.TOP;
      if (this.sides.includes(Sides.TOP) && this.sides.includes(Sides.RIGHT))
        result = Sides.RIGHT;
    }

    // Absoluter Fallback
    if (result === undefined) {
      return Sides.LEFT;
    }

    return result;
  }

  /**
   * Gibt den Rotationswinkel zurück, der benötigt wird, um von der leftHandSide-Ausrichtung
   * zur Kantenausrichtung zu gelangen.
   *
   * @param preferedSide Die Seite auf die gedreht werden soll, dies kann im Voraus mit getLeftHandSide bestimmt werden
   * @returns Rotationswinkel in Grad (-90, 0, 90, 180)
   */
  public getOrientation(preferedSide: Sides): number {
    const activeSide: Sides = preferedSide;
    // Überprüfen, ob die preferedSide in this.sides vorhanden ist
    if (!this.sides.includes(preferedSide)) {
      throw new Error(
        `Die angegebene preferedSide "${preferedSide}" ist nicht in den Seiten der Kante enthalten.`,
      );
    }

    // Bestimme die andere Kante (otherSide)
    const otherSide = this.sides.find((side) => side !== activeSide);

    // Wenn keine andere Kante gefunden wurde (sollte nicht passieren)
    if (otherSide === undefined) {
      return 0;
    }

    // FRONT als activeSide
    if (activeSide === Sides.FRONT) {
      if (otherSide === Sides.LEFT) return 90;
      if (otherSide === Sides.RIGHT) return -90;
      if (otherSide === Sides.TOP) return 180;
      if (otherSide === Sides.BOTTOM) return 0;
    }

    // BACK als activeSide
    if (activeSide === Sides.BACK) {
      if (otherSide === Sides.LEFT) return -90;
      if (otherSide === Sides.RIGHT) return 90;
      if (otherSide === Sides.TOP) return 180;
      if (otherSide === Sides.BOTTOM) return 0;
    }

    // LEFT als activeSide
    if (activeSide === Sides.LEFT) {
      if (otherSide === Sides.FRONT) return -90;
      if (otherSide === Sides.BACK) return 90;
      if (otherSide === Sides.TOP) return 180;
      if (otherSide === Sides.BOTTOM) return 0;
    }

    // RIGHT als activeSide
    if (activeSide === Sides.RIGHT) {
      if (otherSide === Sides.FRONT) return 90;
      if (otherSide === Sides.BACK) return -90;
      if (otherSide === Sides.TOP) return 180;
      if (otherSide === Sides.BOTTOM) return 0;
    }

    // TOP als activeSide
    if (activeSide === Sides.TOP) {
      if (otherSide === Sides.FRONT) return 0;
      if (otherSide === Sides.BACK) return 180;
      if (otherSide === Sides.LEFT) return 90;
      if (otherSide === Sides.RIGHT) return -90;
    }

    // BOTTOM als activeSide
    if (activeSide === Sides.BOTTOM) {
      if (otherSide === Sides.FRONT) return 180;
      if (otherSide === Sides.BACK) return 0;
      if (otherSide === Sides.LEFT) return 90;
      if (otherSide === Sides.RIGHT) return -90;
    }

    // Fallback (sollte nie erreicht werden)
    return 0;
  }

  // sortFunction for connected edges, returns whether the edge ends in the next edge
  public pointsTo(edge2: EdgeInfos): boolean {
    return this.end.innerPos.equals(edge2.start.innerPos);
  }
  // checks if two edges have the same endpoints
  public equals(edge2: EdgeInfos) {
    if (
      edge2.start.innerPos.equals(this.start.innerPos) &&
      edge2.end.innerPos.equals(this.end.innerPos)
    ) {
      return true;
    }
    if (
      edge2.start.innerPos.equals(this.end.innerPos) &&
      edge2.end.innerPos.equals(this.start.innerPos)
    ) {
      return true;
    }
    return false;
  }
  // returns a point along the outer positions of an edge
  public lerp(where: number): THREE.Vector3 {
    return this.start.outerPos
      .clone()
      .add(this.direction.multiplyScalar(this.outerLength * where));
  }
}
